import { useState } from "react";
import { toast } from "sonner";
import { Keyboard, Moon, Sun, RotateCcw } from "lucide-react";
import { usePrefs } from "@/context/PrefsContext";

const NAMES = [["new_call", "New call (Draft)"], ["officer_initiated", "Officer-initiated activity"], ["search", "Search history"], ["entity_search", "Entity search (plates, phones…)"], ["map", "Open map"], ["command_bar", "Focus command bar"], ["commit", "Commit / Send draft"]];

function comboFromEvent(e) {
  const parts = [];
  if (e.ctrlKey || e.metaKey) parts.push("Ctrl");
  if (e.altKey) parts.push("Alt");
  if (e.shiftKey) parts.push("Shift");
  const k = e.key;
  if (["Control", "Shift", "Alt", "Meta"].includes(k)) return null;
  parts.push(k.length === 1 ? k : k);
  return parts.join("+");
}

export function PreferencesCard() {
  const { prefs, theme, setTheme, hotkeys, setHotkey, settings } = usePrefs();
  const [capturing, setCapturing] = useState(null);
  const agency = { new_call: "F1", officer_initiated: "F2", search: "F3", entity_search: "F4", map: "F5", command_bar: "/", commit: "Ctrl+Enter", ...(settings.hotkeys || {}) };
  const onKey = (name) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.key === "Escape") { setCapturing(null); return; }
    const combo = comboFromEvent(e);
    if (!combo) return;
    setHotkey(name, combo);
    setCapturing(null);
    toast.success(`${NAMES.find(([n]) => n === name)[1]} → ${combo}`);
  };
  return (
    <div className="space-y-3" data-testid="preferences-card">
      <div><div className="overline-label">My preferences</div><div className="display text-lg font-semibold">Theme & hotkeys</div><div className="text-xs text-muted-foreground">Saved to your account — your setup returns on next login.</div></div>
      <div>
        <div className="overline-label mb-1">Theme</div>
        <div className="flex gap-1.5">
          <button data-testid="pref-theme-dark" onClick={() => setTheme("dark")} className={`h-8 px-3 rounded-md border text-xs flex items-center gap-1 ${theme === "dark" ? "bg-primary/15 border-primary text-primary" : ""}`}><Moon className="h-3.5 w-3.5" /> Dark (night shift)</button>
          <button data-testid="pref-theme-light" onClick={() => setTheme("light")} className={`h-8 px-3 rounded-md border text-xs flex items-center gap-1 ${theme === "light" ? "bg-primary/15 border-primary text-primary" : ""}`}><Sun className="h-3.5 w-3.5" /> Light</button>
        </div>
      </div>
      <div>
        <div className="overline-label mb-1 flex items-center gap-1"><Keyboard className="h-3 w-3" /> Hotkeys — click a key, then press the new combination (Esc to cancel)</div>
        <div className="space-y-1">
          {NAMES.map(([name, label]) => (
            <div key={name} className="flex items-center gap-2 text-xs">
              <span className="w-48">{label}</span>
              <button data-testid={`pref-hotkey-${name}`} onKeyDown={capturing === name ? onKey(name) : undefined} onClick={() => setCapturing(name)}
                className={`mono h-7 min-w-[110px] px-2 rounded-md border text-left ${capturing === name ? "border-primary ring-2 ring-primary/30" : ""}`}>
                {capturing === name ? "press keys…" : hotkeys[name]}
              </button>
              {prefs.hotkeys?.[name] && prefs.hotkeys[name] !== agency[name] && (
                <button data-testid={`pref-hotkey-reset-${name}`} onClick={() => setHotkey(name, agency[name])} title="Reset to agency default" className="text-muted-foreground hover:text-foreground flex items-center gap-0.5"><RotateCcw className="h-3 w-3" /> agency: {agency[name]}</button>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="text-[11px] text-muted-foreground">Pane sizes are saved automatically as you drag the dividers.</div>
    </div>
  );
}
